import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Send, Sparkles, Target, Gift } from 'lucide-react';
import { LOGO_URL } from '../data/mockData';
import { submitLead } from '../lib/submitLead';

const EnquireModal = ({ isOpen, onClose, context }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const propertyLabel = context?.property || 'General Enquiry';
  const sourceLabel = context?.source || 'Enquire Modal';

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setLoading(false);
      setError('');
    }
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        property: propertyLabel,
        source: sourceLabel,
        page: window.location.pathname || '/',
      };

      await submitLead('/api/enquiry', payload);

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        onClose();
        setFormData({ name: '', email: '', phone: '' });
      }, 2000);
    } catch (submitError) {
      console.error('Error submitting enquiry form:', submitError);
      setError('Failed to submit enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg w-full max-w-md mx-4 shadow-2xl border-2 border-[#DAA520]/30 animate-modal-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors z-10"
        >
          <X size={18} />
        </button>

        <div className="p-8">
          {/* Logo */}
          <div className="text-center mb-6">
            <img src={LOGO_URL} alt="Livelong Wealth" className="h-14 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900">Exclusive Offer!</h3>
            <p className="text-gray-600 text-sm mt-2">
              Get <span className="font-bold text-[#DAA520]">early access</span> to our luxury listings and a{' '}
              <span className="font-bold text-[#DAA520]">free consultation</span>.
            </p>
          </div>

          {/* Benefits */}
          <div className="flex justify-center gap-4 mb-6">
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <Sparkles size={14} className="text-[#DAA520]" />
              VIP Access
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <Target size={14} className="text-[#DAA520]" />
              Expert Advice
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <Gift size={14} className="text-[#DAA520]" />
              No Obligation
            </div>
          </div>

          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-900">Thank You!</h4>
              <p className="text-gray-600 text-sm mt-1">Our team will reach out to you shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {error}
                </div>
              )}
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your Name"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#DAA520] focus:ring-1 focus:ring-[#DAA520]/30 transition-colors bg-gray-50"
                />
              </div>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Email Address"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#DAA520] focus:ring-1 focus:ring-[#DAA520]/30 transition-colors bg-gray-50"
                />
              </div>
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="Phone Number"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#DAA520] focus:ring-1 focus:ring-[#DAA520]/30 transition-colors bg-gray-50"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-[#DAA520] text-black font-semibold tracking-wider text-sm rounded-lg hover:bg-[#C49B17] transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent" />
                    SUBMITTING...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    CLAIM EXCLUSIVE ACCESS
                  </>
                )}
              </button>
              <p className="text-center text-gray-400 text-xs">
                By submitting, you agree to receive communications from Livelong Wealth.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnquireModal;
